import useAppContext from 'flow/hooks/useAppContext';
import { allowCtiCommunication } from 'flow/selectors/cti-integration-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import { isEmbeddedTypeCti } from 'flow/utils/app-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { hasCtiSettings } from 'flow/utils/cti-settings-utils';
import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    Route,
    Redirect,
    RouteProps,
    useParams,
    generatePath,
} from 'react-router-dom';

interface PrivateRouteProps extends RouteProps {
    component: React.ComponentType<any>;
    hasAccess: boolean;
    redirectTo: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
    component: Component,
    hasAccess,
    redirectTo,
    ...rest
}) => (
    <Route
        {...rest}
        render={(props) =>
            hasAccess ? <Component {...props} /> : <Redirect to={redirectTo} />
        }
    />
);

type Requirement = 'dashboard' | 'cti' | 'mobile';

interface PrivateSettingsRouteProps extends RouteProps {
    component: React.ComponentType<any>;
    requirement: Requirement;
}

export const PrivateSettingsRoute: React.FC<PrivateSettingsRouteProps> = ({
    component: Component,
    requirement,
    ...rest
}) => {
    const { embedded } = useAppContext();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    const ctiSettingExist = useAppSelector((state) =>
        allowCtiCommunication(state) ? hasCtiSettings(state) : false,
    );
    const retailerId = useAppSelector((state) =>
        state.customer?.getIn([extensionKey, 'retailer', 'key']),
    );

    const dashboard = useAppSelector((state) =>
        state.wallboards.get('hasAccess'),
    );

    // Hide for Etisalat and Rainbow users
    const mobile = !IS_UAE_HOST && !['retailer-1211'].includes(retailerId);

    const cti = isEmbeddedTypeCti(embedded) && ctiSettingExist;

    const hasAccessTo = (requirement: Requirement) => {
        const requirements = {
            dashboard,
            mobile,
            cti,
        };

        return !!requirements[requirement];
    };

    return (
        <Route
            {...rest}
            render={(props) => (
                <PrivateRoute
                    component={Component}
                    redirectTo={generatePath(routes['settings.account'], {
                        extensionKey,
                    })}
                    hasAccess={hasAccessTo(requirement)}
                    {...props}
                />
            )}
        />
    );
};
